/**
 * @file InvoiceVariationFlagPanel.js
 * @version 1.0.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-11-03
 * @modified 2026-11-03
 * @description The invoice window's block for an invoice flagged from the
 *              Invoice Detail report's variation review (Cam, 3 Nov 2026 —
 *              "flag the invoice in the alerts with the note until the person
 *              that flagged it clears the flag"). Each open flag: its note,
 *              who flagged it and when, whether the invoice was made a draft,
 *              and "Clear flag" — greyed with the reason for anyone but the
 *              person who flagged it (or an admin). Clearing a draft's flag
 *              approves the invoice again (asked first).
 *              POST /api/reports/invoice-variation-flags/:id/clear.
 */

import React, { useState } from 'react';
import ConfirmDialog from './ConfirmDialog';
import { API_URL } from '../config';
import { fetchNoticeSummary } from '../hooks/useNoticeAlerts';
import './InvoiceCorrectionPanel.css';

function InvoiceVariationFlagPanel({ flags, readOnly = false, readOnlyReason = null, onCleared }) {
  const [asking, setAsking] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  if (!Array.isArray(flags) || !flags.length) return null;

  const clear = async (f) => {
    setAsking(null);
    setBusy(true);
    setError('');
    try {
      const res = await fetch(`${API_URL}/api/reports/invoice-variation-flags/${f.id}/clear`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({})
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body.error || `HTTP ${res.status}`);
      try { await fetchNoticeSummary(); } catch (_) { /* the next poll will */ }
      if (onCleared) onCleared(body);
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="invoice-correction-panel invoice-variation-flag-panel" role="status">
      <div className="invoice-correction-head">
        <span className="material-symbols-rounded">flag</span>
        <strong>Flagged from the variation review</strong>
      </div>
      {flags.map(f => {
        const why = readOnly ? (readOnlyReason || 'View only') : (f.clear_block || (busy ? 'Saving…' : null));
        return (
          <div key={f.id} className="invoice-correction-flag" data-flag={f.id}>
            <p>{f.note}</p>
            <p className="invoice-correction-meta">
              {f.made_draft ? 'Made a draft when flagged · ' : ''}
              Flagged{f.flagged_by_name ? ` by ${f.flagged_by_name}` : ''}
              {f.flagged_at ? ` on ${new Date(f.flagged_at).toLocaleDateString('en-GB')}` : ''}
              {f.item_code || f.description ? ` · ${[f.item_code, f.description].filter(Boolean).join(' ')}` : ''}
            </p>
            <button
              type="button"
              className={`btn-slideout${why ? ' btn-disabled' : ''}`}
              onClick={() => setAsking(f)}
              disabled={!!why}
              title={why || (f.approves_again ? 'Approve the invoice again and clear the flag' : 'Clear the flag — it leaves the Alerts')}
            >
              <div className="icon-wrapper"><span className="material-symbols-rounded">{f.approves_again ? 'task_alt' : 'flag'}</span></div>
              <span className="btn-label">{f.approves_again ? 'Approve again & clear flag' : 'Clear flag'}</span>
            </button>
          </div>
        );
      })}
      {error && <p className="invoice-correction-meta" role="alert">{error}</p>}
      <ConfirmDialog
        isOpen={!!asking}
        type="warning"
        title={asking?.approves_again ? 'Approve it again?' : 'Clear the flag?'}
        message={asking?.approves_again
          ? `${asking?.invoice_no} was made a draft when it was flagged. Clearing the flag approves it again — it goes back on statements and to Xero.`
          : `Clear the flag on ${asking?.invoice_no}? It leaves the Alerts for everyone.`}
        confirmText={asking?.approves_again ? 'Approve and clear' : 'Clear flag'}
        confirmIcon={asking?.approves_again ? 'task_alt' : 'flag'}
        cancelText="Not now"
        onConfirm={() => clear(asking)}
        onCancel={() => setAsking(null)}
      />
    </div>
  );
}

export default InvoiceVariationFlagPanel;
